import * as React from 'react';
import type { ReactNode } from 'react';

import { Button, type ButtonProps } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

type TooltipSide = 'top' | 'right' | 'bottom' | 'left';
type TooltipAlign = 'start' | 'center' | 'end';

export interface TooltipIconButtonProps extends Omit<ButtonProps, 'asChild' | 'children'> {
    children: ReactNode;
    delayDuration?: number;
    label: string;
    side?: TooltipSide;
    align?: TooltipAlign;
    tooltip?: ReactNode;
    tooltipClassName?: string;
}

const TooltipIconButton = React.forwardRef<HTMLButtonElement, TooltipIconButtonProps>(
    (
        {
            children,
            delayDuration = 150,
            label,
            side,
            align,
            tooltip,
            tooltipClassName,
            type = 'button',
            variant = 'ghost',
            size = 'iconSm',
            'aria-label': ariaLabel,
            ...props
        },
        ref,
    ) => {
        return (
            <TooltipProvider delayDuration={delayDuration}>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button
                            ref={ref}
                            type={type}
                            variant={variant}
                            size={size}
                            aria-label={ariaLabel || label}
                            {...props}
                        >
                            {children}
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent side={side} align={align} className={tooltipClassName}>
                        {tooltip ?? label}
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        );
    },
);

TooltipIconButton.displayName = 'TooltipIconButton';

export { TooltipIconButton };
